import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import ReportHeader from "../../../reports/components/ReportHeader";
import Loader from "../../../../components/Loader";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { useOrganizationStore } from "../../../../store/organization";
import { userStore } from "../../../../store/user";
import { ACCESS_SCANS_DASHBOARD_STATS } from "../../../reports/access/access-scans-report/fragment";
import { ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import moment from "moment";
import { UsersIcon } from "@heroicons/react/24/outline";
import { fetchShuttleTicketNames, ingressConditions, egressConditions } from "../../accessGateFilters";

const client = createApolloClient();
const { getState: getUserStore } = userStore;

// Gauge component for success rate
const SuccessRateGauge = ({
  successRate,
  totalScans,
  successCount,
  failedCount
}: {
  successRate: number;
  totalScans: number;
  successCount: number;
  failedCount: number;
}) => {
  const getColor = (rate: number) => {
    if (rate >= 90) return '#059669'; // Green
    if (rate >= 70) return '#FFA000'; // Amber
    return '#dc2626'; // Red
  };

  return (
    <div className="flex flex-col items-center justify-center h-full">
      <div style={{ width: '100%', height: '280px', position: 'relative' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={[
                { value: successRate, color: getColor(successRate) },
                { value: 100 - successRate, color: '#e5e7eb' }
              ]}
              cx="50%"
              cy="65%"
              startAngle={180}
              endAngle={0}
              innerRadius="70%"
              outerRadius="95%"
              dataKey="value"
              stroke="none"
            >
              {[
                { value: successRate, color: getColor(successRate) },
                { value: 100 - successRate, color: '#e5e7eb' }
              ].map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center" style={{ top: '40%' }}>
          <div className="text-center">
            <div className="text-5xl font-bold" style={{ color: getColor(successRate) }}>
              {successRate.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600 mt-1">Success Rate</div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center gap-8 mt-2">
        <div className="text-center">
          <div className="text-2xl font-semibold text-gray-800">{totalScans.toLocaleString()}</div>
          <div className="text-xs text-gray-500">Total Scans</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-semibold" style={{ color: '#059669' }}>
            {successCount.toLocaleString()}
          </div>
          <div className="text-xs text-gray-600">Successful</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-semibold" style={{ color: '#dc2626' }}>
            {failedCount.toLocaleString()}
          </div>
          <div className="text-xs text-gray-600">Failed</div>
        </div>
      </div>
    </div>
  );
};

const AccessCard = () => {
  const organizationId = useOrganizationStore((s) => s.organizationId);
  const user = getUserStore().user;
  const [isLoading, setIsLoading] = useState(true);
  const [currentAttendance, setCurrentAttendance] = useState(0);
  const [ingressCount, setIngressCount] = useState(0);
  const [egressCount, setEgressCount] = useState(0);
  const [stats, setStats] = useState({
    totalScans: 0,
    successCount: 0,
    failedCount: 0,
    successRate: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Build where clause for today's scans (5am to 5am next day)
        const now = moment();
        let dayStart: moment.Moment;

        if (now.hour() < 5) {
          // Before 5am today, get yesterday 5am to today 5am
          dayStart = moment().subtract(1, 'day').hour(5).minute(0).second(0);
        } else {
          // After 5am today, get today 5am to tomorrow 5am
          dayStart = moment().hour(5).minute(0).second(0);
        }

        const dayEnd = moment(dayStart).add(1, 'day');
        const todayStart = dayStart.format('YYYY-MM-DD HH:mm:ss');
        const todayEnd = dayEnd.format('YYYY-MM-DD HH:mm:ss');

        // Build base conditions
        let baseConditions: any[] = [
          { organization_id: { _eq: organizationId } },
          { adjusted_scan_time: { _gte: todayStart } },
          { adjusted_scan_time: { _lte: todayEnd } },
        ];

        if (user?.events) {
          baseConditions.push({ event_id: { _in: user?.events } });
        }

        const baseWhere = { _and: baseConditions };

        // Build specific where clauses for aggregation
        const successWhere = {
          _and: [
            ...baseConditions,
            { scan_status: { _ilike: 'success' } }
          ]
        };

        const failWhere = {
          _and: [
            ...baseConditions,
            { scan_status: { _ilike: 'fail' } }
          ]
        };

        // Shuttle tickets are boarding scans, not gate entries (see accessGateFilters.ts)
        const shuttleTicketNames = await fetchShuttleTicketNames(client, organizationId, user?.events);

        // Ingress = successful scan through an access gate into the park
        const ingressWhere = {
          _and: [...baseConditions, ...ingressConditions(shuttleTicketNames)]
        };

        // Egress = successful scan out through an egress gate
        const egressWhere = {
          _and: [...baseConditions, ...egressConditions()]
        };

        // Single query with all aggregates
        const { data } = await client.query({
          query: ACCESS_SCANS_DASHBOARD_STATS,
          variables: {
            where: baseWhere,
            successWhere,
            failWhere,
            ingressWhere,
            egressWhere,
          },
          fetchPolicy: 'network-only', // Always get fresh data
        });

        const totalScans = data?.total?.aggregate?.count || 0;
        const successCount = data?.success?.aggregate?.count || 0;
        const failedCount = data?.failed?.aggregate?.count || 0;
        const ingress = data?.ingress?.aggregate?.count || 0;
        const egress = data?.egress?.aggregate?.count || 0;
        const successRate = totalScans > 0 ? (successCount / totalScans) * 100 : 0;

        setStats({
          totalScans,
          successCount,
          failedCount,
          successRate,
        });

        setIngressCount(ingress);
        setEgressCount(egress);
        setCurrentAttendance(Math.max(0, ingress - egress));
      } catch (error) {
        console.error("Error fetching access data:", error);
        toast.error("Error fetching access data.");
      } finally {
        setIsLoading(false);
      }
    };

    if (organizationId || user) fetchData();
  }, [organizationId, user]);

  return (
    <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3 p-5 bg-white shadow">
      {/* Widget 1: On the Grounds */}
      <div className="w-full rounded-lg grid grid-cols-1 md:grid-cols-1 bg-gray-200 shadow">
        <div className="justify-items-center">
          <ReportHeader
            title="On the Grounds"
            toolTipShow={true}
            tooltipText={
              "Estimated number of people on the grounds right now. Successful scans through the access gates into the park, minus successful egress scans (5am to 5am). Shuttle ticket scans are boarding checks, not gate entries, and are not counted."
            }
          />
        </div>
        <div className="bg-white rounded-md p-1 pb-14 ml-3 mr-3 mb-2">
          {isLoading ? (
            <div className="w-full grid justify-items-center content-center">
              <Loader />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center pt-4">
              <div className="flex flex-col items-center justify-center mb-8 py-12">
                <UsersIcon className="w-16 h-16 text-gray-400 mb-3" />
                <div className="text-6xl font-bold" style={{ color: '#B71C1C' }}>
                  {currentAttendance.toLocaleString()}
                </div>
              </div>
              <div className="text-sm text-gray-500 flex items-center gap-3">
                <span className="text-green-600 font-semibold">↑ {ingressCount.toLocaleString()} In</span>
                <span className="text-gray-300">|</span>
                <span className="text-blue-600 font-semibold">↓ {egressCount.toLocaleString()} Out</span>
                <span className="text-gray-300">|</span>
                <span>Updated: {moment().format('h:mm A')}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Widget 2: Success Rate Gauge */}
      <div className="w-full rounded-lg grid grid-cols-1 md:grid-cols-1 bg-gray-200 shadow">
        <div className="justify-items-center pb-3">
          <ReportHeader
            title={`Scan Performance for ${moment().format('MMM DD, YYYY')}`}
            toolTipShow={true}
            tooltipText={
              "Overall scanning performance for today (5am to 5am). Shows the success rate as a gauge with total scan count, successful scans (green), and failed scans (red)."
            }
          />
        </div>
        <div className="bg-white rounded-md p-1 pb-14 ml-3 mr-3 mb-2">
          {isLoading ? (
            <div className="w-full grid justify-items-center content-center">
              <Loader />
            </div>
          ) : (
            <SuccessRateGauge
              successRate={stats.successRate}
              totalScans={stats.totalScans}
              successCount={stats.successCount}
              failedCount={stats.failedCount}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessCard;
